import { Route, Routes } from "react-router-dom";
import Home from "@/pages/admin/dashboard";
import { MainLayout } from "@/layouts/MainLayout";
import { DealerIntakeForm } from "@/pages/dealer-intake/form/dealer-intake";
import { DealerIntakeFormSuccess } from "@/pages/dealer-intake/form/dealer-intake-success";
import { DealerIntakeLayout } from "@/layouts/DealerIntakeLayout";
import ActionsPage from "@/pages/admin/rules/actions";
import ActionsDetails from "@/pages/admin/rules/actions/details/actions-details";
import ActionCreate from "@/pages/admin/rules/actions/create/actions-create";
import ActionEdit from "@/pages/admin/rules/actions/edit/actions-edit";
import { rulesRoutes } from "@/routes/rulesRoutes";
import UserPermissionsPage from "@/pages/admin/users/permissions";
import UserPermissionDetails from "@/pages/admin/users/permissions/details/user-permission-details";
import UserPermissionCreate from "@/pages/admin/users/permissions/create/user-permission-create";
import UserPermissionEdit from "@/pages/admin/users/permissions/edit/user-permission-edit";
import { PageNotFound } from "@/pages/errors/page-not-found";
import { useHasRole } from "@/lib/helpers/roles";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { dossiersRoutes } from "./routes/dossiersRoutes";
import { analyticsRoutes } from "./routes/analyticsRoutes";
import { affiliatesRoutes } from "./routes/affiliatesRoutes";
import { usersRoutes } from "./routes/usersRoutes";
import { kanbanRoutes } from "./routes/kanbanRoutes";
import { claimsRoutes } from "./routes/claimsRoutes";
import { chatRoutes } from "./routes/chatRoutes";
import { agentsRoutes } from "./routes/agentsRoutes";
import { tasksRoutes } from "./routes/tasksRoutes";
import { emailTemplatesRoutes } from "./routes/emailTemplatesRoutes";
import { servicePartnersRoutes } from "./routes/servicePartnersRoutes";
import { smsTemplatesRoutes } from "./routes/smsTemplatesRoutes";
import { documentTemplatesRoutes } from "./routes/documentTemplatesRoutes";
import { externalWebhooksRoutes } from "./routes/externalWebhooksRoutes";
import { imageCapturesRoutes } from "./routes/imageCapturesRoutes";
import { companiesRoutes } from "./routes/companiesRoutes";
import { emailTemplateLayoutsRoutes } from "./routes/emailTemplateLayoutsRoutes";
import { appointmentTimeSlotsRoutes } from "./routes/appointmentTimeSlotsRoutes";
import { LicensesSelect } from "./pages/admin/app/LicensesSelect";
import { PendingUser } from "./pages/admin/pending/PendingUser";
import { replacementTransportRoutes } from "./routes/replacementTransportRoutes";
import { documentLibrariesRoutes } from "./routes/documentLibrariesRoutes";
import { rolesRoutes } from "./routes/rolesRoutes";
import { faqRoutes } from "./routes/faqRoutes";
import { formConfigurationsRoutes } from "./routes/formConfigurationsRoutes";
import { DealerIntake } from "./pages/admin/dealer-intake/DealerIntake";
import { servicePartnersCollectionsRoutes } from "./routes/servicePartnersCollectionsRoutes";
import { servicePartnersTagsRoutes } from "@/routes/servicePartnersTagsRoutes.tsx";
import { knowledgeBaseRoutes } from "./routes/knowledgeBaseRoutes";
import { baseServicePartnersRoutes } from "./routes/baseRoutes";
import { evaluationGroupsRoutes } from "./routes/evaluationGroupsRoutes";
import { benchmarkRoutes } from "./routes/benchmarkRoutes";
import { aiProfilesRoutes } from "./routes/aiProfilesRoutes";

function App() {
  const hasAdminRole = useHasRole("ROLE_OIDC_ADMIN");

  return (
    <Routes>
      <Route element={<LicensesSelect />} index />
      <Route path="/pending" element={<PendingUser />} />

      {/* Refactored using the new structure and antd layout */}
      <Route path="/" element={<DashboardLayout />}>
        <Route path="analytics">
          {analyticsRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="claims">
          {claimsRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
          <Route path="tasks">
            {tasksRoutes?.map((route) => (
              <Route
                key={route.id}
                path={route.path}
                index={route.index}
                element={route.element}
              />
            ))}
          </Route>
        </Route>
        <Route path="agents">
          {agentsRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="ai-profiles">
          {aiProfilesRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="chat">
          {chatRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="knowledge-base">
          {knowledgeBaseRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="benchmark">
          {benchmarkRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="/dashboard" element={<Home />} />
        <Route path="dossiers">
          {dossiersRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="appointment-time-slots">
          {appointmentTimeSlotsRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="document-libraries">
          {documentLibrariesRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="replacement-transport">
          {replacementTransportRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="users">
          {usersRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="roles">
          {rolesRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="kanban">
          {kanbanRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="service-partners/partners">
          {servicePartnersRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="service-partners/collections">
          {servicePartnersCollectionsRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="service-partners/tags">
          {servicePartnersTagsRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="faqs">
          {faqRoutes?.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.index}
              element={route.element}
            />
          ))}
        </Route>
        <Route path="dealer-intake-v2" element={<DealerIntake />} />
        {hasAdminRole ? (
          <>
            <Route path="companies">
              {companiesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="affiliates">
              {affiliatesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="base/service-partners">
              {baseServicePartnersRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="email-templates">
              {emailTemplatesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
              <Route path="layouts">
                {emailTemplateLayoutsRoutes?.map((route) => (
                  <Route
                    key={route.id}
                    path={route.path}
                    index={route.index}
                    element={route.element}
                  />
                ))}
              </Route>
            </Route>
            <Route path="sms-templates">
              {smsTemplatesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="external-webhooks">
              {externalWebhooksRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="image-captures">
              {imageCapturesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="rules">
              {rulesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="document-templates">
              {documentTemplatesRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="form-configurations">
              {formConfigurationsRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
            <Route path="evaluation-groups">
              {evaluationGroupsRoutes?.map((route) => (
                <Route
                  key={route.id}
                  path={route.path}
                  index={route.index}
                  element={route.element}
                />
              ))}
            </Route>
          </>
        ) : null}
      </Route>

      <Route element={<DealerIntakeLayout />}>
        <Route path="/dealer-intake" element={<DealerIntakeForm />} />
        <Route
          path="/dealer-intake/success"
          element={<DealerIntakeFormSuccess />}
        />
      </Route>

      <Route element={<MainLayout />}>
        <Route path="/rules/actions" element={<ActionsPage />} />
        <Route path="/rules/actions/create" element={<ActionCreate />} />
        <Route path="/rules/actions/:id" element={<ActionsDetails />} />
        <Route path="/rules/actions/:id/edit" element={<ActionEdit />} />

        <Route path="/users/permissions" element={<UserPermissionsPage />} />
        <Route
          path="/users/permissions/create"
          element={<UserPermissionCreate />}
        />
        <Route
          path="/users/permissions/:id"
          element={<UserPermissionDetails />}
        />
        <Route
          path="/users/permissions/:id/edit"
          element={<UserPermissionEdit />}
        />
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}
export default App;
